import { useState, useEffect } from "react";
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from "lucide-react";
import { Button } from "@/components/ui/button";

interface ImageLightboxProps {
  images: Array<{ src: string; alt: string; title: string; number: string }>;
  currentIndex: number;
  onClose: () => void;
}

const ImageLightbox = ({ images, currentIndex, onClose }: ImageLightboxProps) => {
  const [activeIndex, setActiveIndex] = useState(currentIndex);
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") handlePrevious();
      if (e.key === "ArrowRight") handleNext();
    };

    window.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "unset";
    };
  }, [activeIndex]);

  const handlePrevious = () => {
    setActiveIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
    setZoom(1);
  };

  const handleNext = () => {
    setActiveIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    setZoom(1);
  };

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 0.5, 3));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 0.5, 1));
  };

  const currentImage = images[activeIndex];

  return (
    <div className="fixed inset-0 z-[100] bg-background/95 backdrop-blur-lg animate-fade-in">
      {/* Close Button */}
      <Button
        onClick={onClose}
        variant="ghost"
        aria-label="Stäng bildvisning"
        className="absolute top-4 right-4 z-10 text-gold hover:text-gold-light hover:bg-gold/10"
        size="icon"
      >
        <X size={32} />
      </Button>

      {/* Zoom Controls */}
      <div className="absolute top-4 left-4 z-10 flex gap-2">
        <Button
          onClick={handleZoomOut}
          variant="ghost"
          aria-label="Zooma ut"
          className="text-gold hover:text-gold-light hover:bg-gold/10"
          size="icon"
          disabled={zoom === 1}
        >
          <ZoomOut size={24} />
        </Button>
        <Button
          onClick={handleZoomIn}
          variant="ghost"
          aria-label="Zooma in"
          className="text-gold hover:text-gold-light hover:bg-gold/10"
          size="icon"
          disabled={zoom === 3}
        >
          <ZoomIn size={24} />
        </Button>
      </div>

      {/* Navigation Buttons */}
      <Button
        onClick={handlePrevious}
        variant="ghost"
        aria-label="Föregående bild"
        className="absolute left-4 top-1/2 -translate-y-1/2 text-gold hover:text-gold-light hover:bg-gold/10"
        size="icon"
      >
        <ChevronLeft size={48} />
      </Button>

      <Button
        onClick={handleNext}
        variant="ghost"
        aria-label="Nästa bild"
        className="absolute right-4 top-1/2 -translate-y-1/2 text-gold hover:text-gold-light hover:bg-gold/10"
        size="icon"
      >
        <ChevronRight size={48} />
      </Button>

      {/* Image Container */}
      <div className="flex items-center justify-center h-full p-20">
        <div className="relative overflow-auto max-w-full max-h-full">
          <img
            src={currentImage.src}
            alt={currentImage.alt}
            className="max-w-full max-h-full object-contain transition-transform duration-300 rounded-lg"
            style={{ transform: `scale(${zoom})` }}
          />
        </div>
      </div>

      {/* Image Info */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-center bg-card/80 backdrop-blur px-8 py-4 rounded-lg border border-gold/20">
        {currentImage.number && (
          <p className="text-gold text-sm font-medium mb-1">Nr. {currentImage.number}</p>
        )}
        <h3 className="text-2xl font-serif font-semibold text-gold mb-2">
          {currentImage.title}
        </h3>
        <p className="text-muted-foreground text-sm">
          {activeIndex + 1} / {images.length}
        </p>
      </div>
    </div>
  );
};

export default ImageLightbox;
